import React, { useState, useEffect, CSSProperties } from 'react';
import { login, register, getUserInfo, AuthTokens } from '../adapters/authentication';

const Icon = ({ d, size = 16 }: { d: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

function Showcase() {
  return (
    <div style={{
      background: 'linear-gradient(170deg, #0c1b33 0%, #050b12 100%)',
      color: '#fff',
      padding: '40px 44px',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      <svg width="100%" height="100%" viewBox="0 0 400 600" preserveAspectRatio="xMidYMid slice"
        style={{ position: 'absolute', inset: 0, opacity: 0.10 }}>
        <defs>
          <pattern id="loginGrid" width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M 32 0 L 0 0 0 32" fill="none" stroke="#00c896" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="400" height="600" fill="url(#loginGrid)" />
      </svg>
      <div style={{
        position: 'absolute', top: '-100px', right: '-100px',
        width: 320, height: 320,
        background: 'radial-gradient(circle, rgba(0,200,150,0.30) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-150px', left: '-50px',
        width: 280, height: 280,
        background: 'radial-gradient(circle, rgba(124,58,237,0.20) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: 'linear-gradient(135deg, #00c896, #009B4D)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 800, color: '#0c1b33',
        }}>S</div>
        <div style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.02em' }}>
          smartstreet<span style={{ color: '#00c896' }}>.ai</span>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', zIndex: 1, gap: 14, maxWidth: 380 }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.18em',
          textTransform: 'uppercase', color: '#00c896',
        }}>Reporting Dashboard</div>
        <h1 style={{
          fontSize: 36, fontWeight: 700, lineHeight: 1.1,
          letterSpacing: '-0.02em', color: '#fff',
        }}>
          Proof of performance,<br />
          <span style={{ color: '#00c896' }}>verified by sensors.</span>
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
          Track impressions, dwell time, and audience composition across every screen in the Smartstreet NYC network — in real time.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
          {[
            { icon: 'M3 3v18h18M7 14l3-3 4 4 5-6', label: 'Real-time campaign metrics' },
            { icon: 'M16 11a4 4 0 1 0-8 0M2 21a8 8 0 0 1 16 0M22 21a6 6 0 0 0-10-5.2', label: 'Anonymous audience demographics' },
            { icon: 'M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5l-8-3z', label: 'Privacy-first, sensor-verified' },
          ].map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: 'rgba(0,200,150,0.12)',
                color: '#00c896',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon d={f.icon} size={14} />
              </div>
              {f.label}
            </div>
          ))}
        </div>
      </div>

      <div style={{
        position: 'relative', zIndex: 1,
        padding: '14px 16px',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 12,
        marginTop: 24,
      }}>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.45, marginBottom: 10 }}>
          "Smartstreet's sensor data closed the loop. We finally see who actually watched the ad — not just broad impression numbers."
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'linear-gradient(135deg,#dc2626,#7c3aed)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 800, color: '#fff',
          }}>SF</div>
          <div style={{ fontSize: 11.5 }}>
            <div style={{ fontWeight: 700 }}>Maya Levin</div>
            <div style={{ color: 'rgba(255,255,255,0.55)' }}>Director of Media · Sonia Friedman Productions</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', required }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; required?: boolean;
}) {
  return (
    <div>
      <label style={lbl()}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        style={input()}
      />
    </div>
  );
}

function PasswordStrength({ password }: { password: string }) {
  let score = 0;
  if (password.length >= 10) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const labels = ['Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['#dc2626', '#f59e0b', '#3b82f6', 'var(--brand-green-500)'];
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{
            flex: 1, height: 3, borderRadius: 2,
            background: i < score ? colors[score - 1] : 'var(--bg-surface-3)',
            transition: 'background 0.2s',
          }} />
        ))}
      </div>
      <div style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginTop: 4 }}>
        {password.length === 0
          ? 'Mix letters, numbers & symbols for a strong password'
          : `${labels[Math.max(0, score - 1)]} password`}
      </div>
    </div>
  );
}

function AuthForm({ mode, onLogin }: { mode: 'login' | 'signup'; onLogin?: (tokens: AuthTokens & { user: any }) => void }) {
  const isLogin = mode === 'login';
  const [showPw, setShowPw] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!isLogin && password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        const tokens = await login(email, password);
        const { data: user } = await getUserInfo();
        onLogin?.({ ...tokens, user });
      } else {
        await register({
          email,
          username: email,
          password,
          password2: confirmPassword,
          firstName: first,
          lastName: last,
        });
        setSuccess(true);
        setTimeout(() => { window.location.href = '/login'; }, 1500);
      }
    } catch (err: any) {
      const msg = err?.detail
        ?? err?.non_field_errors?.[0]
        ?? err?.email?.[0]
        ?? err?.username?.[0]
        ?? err?.password?.[0]
        ?? (isLogin ? 'Invalid email or password.' : 'Registration failed. Please try again.');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      padding: '40px 44px',
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg-surface-2)',
      position: 'relative',
    }}>
      <div style={{
        position: 'absolute', top: 28, right: 32,
        fontSize: 12, color: 'var(--fg-muted)',
      }}>
        {isLogin ? "Don't have an account?" : 'Already a member?'}{' '}
        <a href={isLogin ? '/signup' : '/login'} style={{
          color: 'var(--brand-green-500)', fontWeight: 700, textDecoration: 'none',
        }}>{isLogin ? 'Sign up' : 'Log in'}</a>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 420, width: '100%', margin: '0 auto' }}>
        <div style={{ marginBottom: 28 }}>
          <h2 style={{
            fontSize: 28, fontWeight: 700, color: 'var(--fg-strong)',
            letterSpacing: '-0.02em', marginBottom: 6, lineHeight: 1.15,
          }}>
            {isLogin ? 'Welcome back' : 'Create your account'}
          </h2>
          <p style={{ fontSize: 13.5, color: 'var(--fg-muted)' }}>
            {isLogin
              ? 'Sign in to your advertiser workspace to access campaign reports.'
              : 'Get full POP reporting access for your campaigns on the Smartstreet network.'}
          </p>
        </div>


        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!isLogin && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field label="First name" value={first} onChange={setFirst} required />
              <Field label="Last name" value={last} onChange={setLast} required />
            </div>
          )}
          <Field label="Work email" type="email" value={email} onChange={setEmail} placeholder="you@company.com" required />
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <label style={lbl()}>Password</label>
              {isLogin && (
                <a href="/forgot-password" style={{ fontSize: 11.5, color: 'var(--brand-green-500)', fontWeight: 700, textDecoration: 'none' }}>Forgot password?</a>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={isLogin ? 'Enter your password' : 'At least 10 characters'}
                required
                style={{ ...input(), paddingRight: 38 }}
              />
              <button type="button" onClick={() => setShowPw(p => !p)}
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'transparent', border: 'none',
                  color: 'var(--fg-muted)', padding: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center',
                }}
                title={showPw ? 'Hide password' : 'Show password'}
              >
                {showPw
                  ? <Icon d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" size={15} />
                  : <Icon d="M2 2l20 20M9.9 4.2A10 10 0 0 1 12 4c6.5 0 10 7 10 7a18 18 0 0 1-3.4 4.4M6.5 6.5A18 18 0 0 0 2 11s3.5 7 10 7c1.4 0 2.7-.3 3.9-.8" size={15} />}
              </button>
            </div>
            {!isLogin && <PasswordStrength password={password} />}
          </div>

          {!isLogin && (
            <div>
              <label style={lbl()}>Confirm password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Repeat your password"
                required
                style={{
                  ...input(),
                  borderColor: confirmPassword && confirmPassword !== password ? '#dc2626' : undefined,
                }}
              />
              {confirmPassword && confirmPassword !== password && (
                <div style={{ fontSize: 10.5, color: '#dc2626', marginTop: 4 }}>Passwords do not match</div>
              )}
            </div>
          )}


          {!isLogin && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11.5, color: 'var(--fg-muted)', cursor: 'pointer', userSelect: 'none', lineHeight: 1.45 }}>
              <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ accentColor: 'var(--brand-green-500)', width: 14, height: 14, marginTop: 2 }} />
              <span>
                I agree to the <a href="#" style={{ color: 'var(--brand-green-500)', fontWeight: 700 }}>Terms</a> and acknowledge the <a href="#" style={{ color: 'var(--brand-green-500)', fontWeight: 700 }}>Privacy Policy</a>. Smartstreet collects anonymous audience signals only.
              </span>
            </label>
          )}

          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, fontSize: 12.5,
              background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)',
              color: '#dc2626',
            }}>{error}</div>
          )}

          {success && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, fontSize: 12.5,
              background: 'rgba(0,200,150,0.08)', border: '1px solid rgba(0,200,150,0.25)',
              color: 'var(--brand-green-500)',
            }}>Account created! Redirecting to sign in…</div>
          )}

          <button type="submit" disabled={loading || (!isLogin && (!agree || (!!confirmPassword && confirmPassword !== password)))} style={primaryBtn(loading || (!isLogin && (!agree || (!!confirmPassword && confirmPassword !== password))))}>
            {loading
              ? (isLogin ? 'Signing in…' : 'Creating account…')
              : (isLogin ? 'Sign in to dashboard' : 'Create account')}
            {!loading && <Icon d="M5 12h14M13 5l7 7-7 7" size={14} />}
          </button>

        </form>
      </div>
    </div>
  );
}

function lbl(): CSSProperties {
  return {
    display: 'block',
    fontSize: 11, fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    color: 'var(--fg-muted)', marginBottom: 6,
  };
}
function input(): CSSProperties {
  return {
    width: '100%',
    padding: '10px 12px',
    background: 'var(--bg-surface-3)',
    border: '1px solid var(--border-strong)',
    borderRadius: 8,
    fontSize: 13, color: 'var(--fg-strong)',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    boxSizing: 'border-box',
  };
}
function primaryBtn(disabled: boolean): CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '12px 18px',
    background: disabled ? 'var(--bg-surface-3)' : 'var(--brand-navy-800)',
    color: disabled ? 'var(--fg-faint)' : '#fff',
    border: 'none', borderRadius: 10,
    fontSize: 13.5, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit', marginTop: 4,
  };
}

export function AuthShell({ mode, onLogin }: { mode: 'login' | 'signup'; onLogin?: (tokens: AuthTokens & { user: any }) => void }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('pop_theme') || 'light');
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('pop_theme', theme);
  }, [theme]);
  const toggleTheme = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-app)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <button onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} style={{
        position: 'fixed', top: 20, right: 20, zIndex: 10,
        width: 36, height: 36, borderRadius: 10,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-soft)',
        color: 'var(--fg-default)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: 'var(--shadow-card)',
      }}>
        {theme === 'dark'
          ? <Icon d="M12 3v1M12 20v1M4.2 4.2l.7.7M19.1 19.1l.7.7M3 12h1M20 12h1M4.2 19.8l.7-.7M19.1 4.9l.7-.7M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z" size={16} />
          : <Icon d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" size={16} />}
      </button>

      <div style={{
        width: '100%',
        maxWidth: 1080,
        minHeight: 680,
        background: 'var(--bg-surface)',
        borderRadius: 18,
        overflow: 'hidden',
        boxShadow: '0 30px 80px rgba(15,23,42,0.18), 0 4px 12px rgba(15,23,42,0.06)',
        display: 'grid',
        gridTemplateColumns: '480px 1fr',
        border: '1px solid var(--border-subtle)',
      }}>
        <Showcase />
        <AuthForm mode={mode} onLogin={onLogin} />
      </div>
    </div>
  );
}
